import React, { useRef, useEffect, useCallback, useMemo } from "react";
import { connect } from "react-redux";
import merge from "lodash.merge";
import className from "./style.module.scss";
import { PlotWrapper } from "./plots/PlotWrapper";
import boxPlotIcon from "./plots/icons/Box 2.svg";
import barPlotIcon from "./plots/icons/Bar graph.svg";
import histogramPlotIcon from "./plots/icons/Histogram.svg";
import linePlotIcon from "./plots/icons/Line.svg";
import * as PlotSpecs from "./plots/specs";

import {
  initSpreadsheet,
  updateSpreadsheetData,
  updateSpreadsheetOverwrites,
  addSpreadsheetPlot,
  updateSpreadsheetPlot,
  setPlotLocked,
} from "./Reducers/actions";

import { Workbook } from "@fortune-sheet/react";
import "@fortune-sheet/react/dist/index.css";

// Track which spreadsheet instance last updated data to prevent cascade updates
let lastUpdateSource = null;

const ActionTypes = {
  SET_CELLS: "SET_CELLS",
  HIGHLIGHT_CELLS: "HIGHLIGHT_CELLS",
  HIGHLIGHT_HEAD: "HIGHLIGHT_HEAD",
  SET_SELECTION: "SET_SELECTION",
  CHANGE_SHEET: "CHANGE_SHEET",
  PLOT_LOCK: "PLOT_LOCK",
};

const PlotTypes = {
  BASIC_BAR: "BASIC_BAR",
  BOX_MIN_MAX: "BOX_MIN_MAX",
  CONTROL_HISTOGRAM: "CONTROL_HISTOGRAM",
  LINE_BASIC: "LINE_BASIC",
};

/**
 * Spreadsheet Component
 * Manages the rendering and interaction of the Fortune Sheet-based spreadsheet, with integrated
 * Redux state management and data updates for external components.
 */
const Spreadsheet = (props) => {
  const spreadsheetRef = useRef(null);
  const spreadsheetDataRef = useRef(props.spreadsheetData);
  const containerRef = useRef(null);
  const instanceIdRef = useRef(props.sectionId);

  const DefaultTemplateData = [
    {
      name: "Sheet 1",
      row: 20,
      column: 20,
    },
  ];

  const defaultTemplateConfig = {
    showToolbar: true,
    showFormulaBar: true,
    showSheetTabs: true,
    row: 20,
    column: 20,
    toolbarItems: [
      "undo",
      "redo",
      "|",
      "format-painter",
      "clear-format",
      "number-decrease",
      "number-increase",
      "format",
      "|",
      "font-size",
      "bold",
      "horizontal-align",
      "vertical-align",
      "text-wrap",
      "border",
      "merge-cell",
      "|",
      "filter",
      "quick-formula",
    ],
    cellContextMenu: [
      "copy",
      "paste",
      "clear",
      "|",
      "sort",
      "filter",
      "|",
      "insert-row",
      "insert-column",
      "delete-row",
      "delete-column",
      "delete-cell",
    ],
    sheetTabContextMenu: ["move", "|", "color"],
  };

  const templateData =
    props.templateData.length > 0 ? props.templateData : DefaultTemplateData;
  const templateConfig = merge({}, defaultTemplateConfig, props.templateConfig);

  const highlightColors = {
    hint: "#ffcc0080",
    warning: "#ed343180",
    none: null,
  };

  // Attach event listeners to track changes in cell input
  useEffect(() => {
    document.addEventListener("input", handleInputChanges);
    document.addEventListener("click", handleInputChanges);

    return () => {
      document.removeEventListener("input", handleInputChanges);
      document.removeEventListener("click", handleInputChanges);
    };
  }, []);

  // Initialize the spreadsheet when the reference is available
  useEffect(() => {
    if (spreadsheetRef.current) {
      initSpreadsheetData();
    }
  }, [spreadsheetRef]);

  // Update spreadsheet data and handle overwrites when the data changes
  useEffect(() => {
    spreadsheetDataRef.current = props.spreadsheetData;
    handleOverwrites();
    handlePlotUpdates();
  }, [props.spreadsheetData]);

  /**
   * Initializes spreadsheet data and configuration in Redux.
   */
  const initSpreadsheetData = () => {
    const initData = {
      data: templateData,
      config: templateConfig,
      overwrites: props.overwrites,
      queuedOverwrites: [],
      plots: {},
    };
    props.initSpreadsheet(initData);
  };

  /**
   * Updates spreadsheet data in Redux by fetching current sheet data.
   */
  const updateData = useCallback(() => {
    // Mark this instance as the source of the update
    lastUpdateSource = instanceIdRef.current;

    // Ensure data arrays are defined to avoid errors
    const newData =
      spreadsheetRef.current?.getAllSheets().map((sheet) => ({
        ...sheet,
        data:
          sheet.data?.map((rowData) =>
            rowData.map((colData) => ({ ...colData }))
          ) || [],
      })) || [];

    newData.forEach((sheet, index) => {
      if (sheet.id === spreadsheetRef.current?.getSheet()?.id) {
        newData[index].selection =
          spreadsheetRef.current.getSelectionCoordinates()[0];
      }
    });
    props.updateSpreadsheetData(newData);
  }, []);

  /**
   * Handles input changes within the spreadsheet cells and updates live cell values.
   */
  const handleInputChanges = () => {
    const focusedElement = document.activeElement;

    // Only process if the focused element belongs to THIS spreadsheet instance
    if (!containerRef.current?.contains(focusedElement)) {
      return;
    }

    if (
      focusedElement?.classList.contains("fortune-fx-input") ||
      focusedElement?.classList.contains("luckysheet-cell-input")
    ) {
      // Find the input element within THIS container, not globally
      const inputElem =
        containerRef.current?.querySelector(".fortune-fx-input");
      const value = inputElem?.textContent || "";
      const range = spreadsheetRef.current?.getSelection();
      const sheetId = spreadsheetRef.current?.getSheet()?.id;
      if (sheetId && range) {
        updateDataWithLiveCell(
          sheetId,
          range[0].row[0],
          range[0].column[0],
          value
        );
      }
    }
  };

  /**
   * Updates the live cell value within the Redux state for mid-edit changes.
   */
  const updateDataWithLiveCell = (sheetId, row, col, value) => {
    const newData =
      spreadsheetRef.current?.getAllSheets().map((sheet) => ({
        ...sheet,
        data:
          sheet.data?.map((rowData) =>
            rowData.map((colData) => ({ ...colData }))
          ) || [],
      })) || [];

    newData.forEach((sheet, index) => {
      if (sheet.id === sheetId) {
        newData[index].data[row][col].live = value;
        newData[index].selection =
          spreadsheetRef.current.getSelectionCoordinates()[0];
      }
    });
    props.updateSpreadsheetData(newData);
  };

  /**
   * Processes queued overwrites for the spreadsheet data.
   */
  const handleOverwrites = () => {
    if (props.spreadsheetData?.queuedOverwrites?.length > 0) {
      const actions = [];
      let changingView = false;

      props.spreadsheetData.queuedOverwrites.forEach((action) => {
        const currentSheet = spreadsheetRef.current.getSheet();

        switch (action.type) {
          case ActionTypes.SET_CELLS:
            actions.push(action);
            if (action.sheet === currentSheet.id) {
              action.cells.forEach((cell) => {
                if (cell.property === "f") {
                  spreadsheetRef.current.setCellValue(
                    cell.row,
                    cell.col,
                    cell.value
                  );
                } else {
                  spreadsheetRef.current.setCellFormat(
                    cell.row,
                    cell.col,
                    cell.property,
                    cell.value
                  );
                }
              });
            }
            break;

          case ActionTypes.HIGHLIGHT_CELLS:
            actions.push(action);
            if (action.sheet === currentSheet.id) {
              action.cells.forEach((cell) => {
                spreadsheetRef.current.setCellFormat(
                  cell.row,
                  cell.col,
                  "bg",
                  highlightColors[cell.type]
                );
              });
            }
            break;

          case ActionTypes.HIGHLIGHT_HEAD:
            {
              actions.push(action);
              const elements = document.querySelectorAll(
                action.axis === "x"
                  ? ".fortune-row-header"
                  : ".fortune-col-header"
              );
              elements.forEach((element) => {
                element.style.backgroundColor = action.active
                  ? highlightColors.hint
                  : null;
              });
            }
            break;

          case ActionTypes.CHANGE_SHEET:
            changingView = true;
            spreadsheetRef.current.activateSheet({ id: action.id });
            break;

          case ActionTypes.PLOT_LOCK:
            props.setPlotLocked(action.plot);
            break;

          case ActionTypes.SET_SELECTION:
            break;

          default:
            break;
        }
      });

      if (changingView) {
        props.updateSpreadsheetOverwrites(actions);
      } else {
        props.updateSpreadsheetOverwrites([]);
      }
    }
  };

  /**
   * Generates a new plot based on the selected cells and plot type.
   */
  const generatePlot = (type) => {
    const selected = spreadsheetRef.current?.getSelection();
    if (selected) {
      const currentSheet =
        spreadsheetRef.current.getSheet()?.id ?? "default_sheet_id";
      const range = spreadsheetRef.current.getSelectionCoordinates()[0];
      const key = `${currentSheet}_${range}_${type}_plot`;

      const selectedData = spreadsheetRef.current.getCellsByRange(selected[0]);

      let plotSpec;
      switch (type) {
        case PlotTypes.BASIC_BAR:
          plotSpec = PlotSpecs.BarPlotBasic(selectedData);
          break;

        case PlotTypes.BOX_MIN_MAX:
          plotSpec = PlotSpecs.BoxPlotBasic(selectedData);
          break;

        case PlotTypes.CONTROL_HISTOGRAM:
          plotSpec = PlotSpecs.HistogramPlotControl(selectedData);
          break;

        case PlotTypes.LINE_BASIC:
          plotSpec = PlotSpecs.LinePlotBasic(selectedData);
          break;

        default:
          break;
      }

      const {
        spec,
        unitX,
        unitY,
        headerX,
        headerY,
        title,
        histogramBinWidth,
        histogramRangeMin,
        histogramRangeMax,
      } = plotSpec;

      const plot = {
        key: key,
        type: type,
        sheet: currentSheet,
        sheetName: spreadsheetRef.current?.getSheet()?.name,
        title: title,
        headerX: headerX,
        headerY: headerY,
        unitX: unitX,
        unitY: unitY,
        histogramBinWidth: histogramBinWidth,
        histogramRangeMin: histogramRangeMin,
        histogramRangeMax: histogramRangeMax,
        cellsUsed: {
          coords: selected[0],
          range: range,
          data: selectedData,
        },
        spec: spec,
      };

      if (
        !Object.prototype.hasOwnProperty.call(
          spreadsheetDataRef.current?.plots || {},
          key
        )
      ) {
        if (Object.keys(spreadsheetDataRef.current.plots || {}).length < 6) {
          props.addSpreadsheetPlot(plot);
        } else {
          alert("Plot cap reached, remove a plot to generate a new one.");
        }
      }
    }
  };

  /**
   * Updates an existing plot with new data based on changes in the spreadsheet.
   */
  const updatePlot = (plotId, newData) => {
    const plotType = spreadsheetDataRef.current?.plots?.[plotId]?.type;

    let plotSpec;

    if (plotType === PlotTypes.BASIC_BAR) {
      plotSpec = PlotSpecs.BarPlotBasic(newData);
    } else if (plotType === PlotTypes.BOX_MIN_MAX) {
      plotSpec = PlotSpecs.BoxPlotBasic(newData);
    } else if (plotType === PlotTypes.CONTROL_HISTOGRAM) {
      plotSpec = PlotSpecs.HistogramPlotControl(newData);
    } else if (plotType === PlotTypes.LINE_BASIC) {
      plotSpec = PlotSpecs.LinePlotBasic(newData);
    }

    if (plotSpec) {
      const { spec, unitX, unitY } = plotSpec;

      const plotUpdateData = {
        key: plotId,
        unitX: unitX,
        unitY: unitY,
        newData: newData,
        spec: spec,
      };

      if (!spreadsheetDataRef.current?.plots?.[plotId]?.locked) {
        props.updateSpreadsheetPlot(plotUpdateData);
      }
    }
  };

  /**
   * Checks if any plots need to be updated based on current spreadsheet data.
   */
  const handlePlotUpdates = () => {
    // Only process if THIS spreadsheet instance was the source of the data change
    // This prevents multiple instances from all trying to update simultaneously
    if (lastUpdateSource !== instanceIdRef.current) {
      return;
    }

    if (props.spreadsheetData) {
      Object.entries(props.spreadsheetData.plots || {}).forEach(([, plot]) => {
        if (
          plot?.sheet &&
          plot.sheet === spreadsheetRef.current?.getSheet()?.id
        ) {
          const plotData = plot.cellsUsed.data;
          const plotSheetData = spreadsheetRef.current.getCellsByRange(
            plot.cellsUsed.coords
          );

          let hasChanges = false;

          plotData.forEach((row, rowIndex) => {
            row.forEach((cell, colIndex) => {
              if (cell && plotSheetData[rowIndex]?.[colIndex]) {
                if (cell.v !== plotSheetData[rowIndex][colIndex].v) {
                  hasChanges = true;
                }
              } else if (cell !== plotSheetData[rowIndex]?.[colIndex]) {
                hasChanges = true;
              }
            });
          });

          if (hasChanges) {
            updatePlot(plot.id, plotSheetData);
          }
        }
      });
    }
  };

  // Toolbar items with optional filtering based on templateConfig
  let customToolbarItems = [
    {
      key: "BarPlot",
      tooltip: "Generate Bar Graph",
      onClick: () => {
        generatePlot(PlotTypes.BASIC_BAR);
      },
      selected: false,
      icon: <img src={barPlotIcon} alt="Generate Bar Graph" />,
    },
    {
      key: "BoxPlot",
      tooltip: "Generate Box Plot",
      onClick: () => {
        generatePlot(PlotTypes.BOX_MIN_MAX);
      },
      selected: false,
      icon: <img src={boxPlotIcon} alt="Generate Box Plot" />,
    },
    {
      key: "HistogramPlot",
      tooltip: "Generate Histogram",
      onClick: () => {
        generatePlot(PlotTypes.CONTROL_HISTOGRAM);
      },
      selected: false,
      icon: <img src={histogramPlotIcon} alt="Generate Histohram Plot" />,
    },
    {
      key: "LinePlot",
      tooltip: "Generate Line Chart",
      onClick: () => {
        generatePlot(PlotTypes.LINE_BASIC);
      },
      selected: false,
      icon: <img src={linePlotIcon} alt="Generate Line Chart" />,
    },
  ];
  customToolbarItems = Object.prototype.hasOwnProperty.call(
    props.templateConfig,
    "customItems"
  )
    ? customToolbarItems.filter((item) =>
        props.templateConfig.customItems.includes(item.key)
      )
    : customToolbarItems;

  // Memoized FortuneSheet component to prevent unnecessary re-renders
  const FortuneSheet = useMemo(
    () => (
      <Workbook
        ref={spreadsheetRef}
        data={templateData}
        {...templateConfig}
        defaultFontSize={12}
        customToolbarItems={customToolbarItems}
        lang={"en"}
        onChange={updateData}
      />
    ),
    []
  );

  return (
    <div ref={containerRef} className={className.SpreadsheetContainer}>
      {FortuneSheet}
      {props.spreadsheetData?.plots &&
        Object.entries(props.spreadsheetData.plots || {}).map(([key, plot]) => {
          if (
            plot?.sheet &&
            plot.sheet === spreadsheetRef.current?.getSheet()?.id
          ) {
            return (
              <PlotWrapper
                key={key}
                index={key}
                spec={plot.spec}
                plotData={plot}
                plotCount={
                  Object.keys(props.spreadsheetData.plots || {}).length
                }
              />
            );
          }
          return null;
        })}
    </div>
  );
};

/**
 * Maps Redux state to component props.
 */
function mapStateToProps(state, props) {
  return {
    templateData: props.spreadsheet.data,
    templateConfig: props.spreadsheet.config,
    overwrites: props.spreadsheet.overwrites,
    spreadsheetData: state.spreadsheet.spreadsheetData || {},
    sectionId: props.sectionId,
  };
}

/**
 * Maps Redux dispatch actions to component props.
 */
function mapDispatchToProps(dispatch) {
  return {
    initSpreadsheet: (data) => {
      dispatch(initSpreadsheet(data));
    },
    updateSpreadsheetData: (data) => {
      dispatch(updateSpreadsheetData(data));
    },
    updateSpreadsheetOverwrites: (data) => {
      dispatch(updateSpreadsheetOverwrites(data));
    },
    addSpreadsheetPlot: (data) => {
      dispatch(addSpreadsheetPlot(data));
    },
    updateSpreadsheetPlot: (data) => {
      dispatch(updateSpreadsheetPlot(data));
    },
    setPlotLocked: (data) => dispatch(setPlotLocked(data)),
  };
}

const mapped_Spreadsheet = connect(
  mapStateToProps,
  mapDispatchToProps
)(Spreadsheet);

export { mapped_Spreadsheet as Spreadsheet };
